import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { BaseService, PaginatedResponse } from '@tcc-mono/shared/data-access';
import { Observable } from 'rxjs';

export type KoppelGrowthType = 'witvlees' | 'starters' | 'rose';

export interface GrowthTypeIndex {
  koppel_type: KoppelGrowthType;
  animals_with_growth: number;
  avg_growth_per_day: number | null;
  growth_index_pct: number | null;
}

export type GrowthIndexByType = Record<KoppelGrowthType, GrowthTypeIndex>;

export const GROWTH_INDEX_TYPES: KoppelGrowthType[] = ['witvlees', 'starters', 'rose'];

export function formatGrowthIndexPct(value: number | null | undefined): string {
  if (value == null) {
    return '—';
  }
  return `${Math.abs(value).toLocaleString('nl-NL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

export function growthIndexTone(value: number | null | undefined): 'up' | 'down' | 'neutral' {
  if (value == null || value === 0) {
    return 'neutral';
  }
  return value > 0 ? 'up' : 'down';
}

export function growthIndexCssClass(value: number | null | undefined): string {
  const tone = growthIndexTone(value);
  if (tone === 'up') {
    return 'growth-index-up';
  }
  if (tone === 'down') {
    return 'growth-index-down';
  }
  return '';
}

export interface OriginUbnStats {
  origin_ubn: string;
  total_animals: number;
  animals_with_growth: number;
  avg_growth_per_day: number | null;
  avg_start_weight?: number | null;
  avg_slaughter_weight?: number | null;
  avg_days_on_stable?: number | null;
  culled_animals: number;
  unit: 'g_per_day';
  by_type?: GrowthIndexByType;
}

export interface OriginUbnStableMeta {
  id: number;
  name: string | null;
  relation_name: string | null;
}

export interface OriginUbnStatistic extends OriginUbnStats {
  calculated_at: string | null;
  stable: OriginUbnStableMeta | null;
}

export interface OriginUbnStatsResponse {
  data: OriginUbnStats;
}

export type OriginUbnTraderPeriod = 'year' | 'all';

export interface OriginUbnTraderRelation {
  id: number;
  name: string;
  relation_number: string | null;
  animals_count: number;
  rides_count: number;
  last_loaded_at: string | null;
}

export interface OriginUbnTraderRidePurchaseOrder {
  id: number;
  order_number: string;
  relation_id: number;
  relation_name: string | null;
  relation_mismatch: boolean;
}

export interface OriginUbnTraderRide {
  id: number;
  number: string | null;
  origin: string | null;
  license_plate: string | null;
  destination_ubn: string | null;
  destination_name: string | null;
  relation_id: number;
  relation_name: string | null;
  animals_count: number;
  loaded_at: string | null;
  purchase_orders: OriginUbnTraderRidePurchaseOrder[];
}

export interface OriginUbnTraderRelationsResponse {
  loading_ubn: string;
  period: OriginUbnTraderPeriod;
  period_from: string | null;
  data: OriginUbnTraderRelation[];
}

export interface OriginUbnTraderRidesResponse {
  loading_ubn: string;
  relation_id: number;
  period: OriginUbnTraderPeriod;
  period_from: string | null;
  data: OriginUbnTraderRide[];
}

export interface OriginUbnStatisticsParams {
  sort?: 'avg_growth_per_day' | 'avg_start_weight' | 'avg_slaughter_weight' | 'avg_days_on_stable' | 'culled_animals' | 'total_animals' | 'animals_with_growth' | 'origin_ubn' | 'growth_index' | 'growth_index_witvlees' | 'growth_index_starters' | 'growth_index_rose';
  direction?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
  q?: string;
  rank?: 'all' | 'top_25' | 'top_10pct' | 'bottom_25';
  rank_type?: KoppelGrowthType;
  min_animals_with_growth?: number;
}

export interface OriginUbnSyncResponse {
  data: {
    queued: boolean;
    origin_ubn: string | null;
    message: string;
  };
}

export interface OriginUbnGrowthCohort {
  month: string;
  avg_growth_per_day: number;
  min_growth_per_day: number;
  max_growth_per_day: number;
  animals_with_growth: number;
  total_animals: number;
  culled_animals: number;
  cull_rate: number;
}

export interface OriginUbnCullMonth {
  month: string;
  count: number;
}

export interface OriginUbnRankedAnimal {
  id: number;
  country_code: string;
  identification_number: string;
  koppel_id: number | null;
  koppel_number: string | null;
  koppel_type: string | null;
  growth_per_day: number;
  arrival_date: string | null;
}

export interface OriginUbnDashboard {
  origin_ubn: string;
  unit: 'g_per_day';
  stable: OriginUbnStableMeta | null;
  growth_by_arrival_month: OriginUbnGrowthCohort[];
  culls_by_month: OriginUbnCullMonth[];
  top_animals: OriginUbnRankedAnimal[];
  bottom_animals: OriginUbnRankedAnimal[];
}

export interface OriginUbnDashboardResponse {
  data: OriginUbnDashboard;
}

export interface OriginUbnAnimal {
  id: number;
  country_code: string;
  identification_number: string;
  work_number?: string | null;
  origin_ubn?: string | null;
  start_weight?: string | null;
  growth_per_day?: number | null;
  status?: string | null;
  arrival_date?: string | null;
  end_date?: string | null;
  days_on_stable?: number | null;
  age_at_arrival?: number | null;
  age_at_end?: number | null;
  setup_code?: string | null;
  gender_code?: string | null;
  gender_name?: string | null;
  color_code?: string | null;
  color_name?: string | null;
  koppel_id?: number | null;
  koppel_number?: string | null;
  koppel_type?: string | null;
  growth_index_pct?: number | null;
  slaughter_list_line?: {
    weight?: string | number | null;
  } | null;
}

export interface OriginUbnAnimalsParams {
  with_growth_only?: boolean;
  koppel_type?: 'witvlees' | 'starters' | 'rose' | '';
  gender?: string;
  color?: string;
  per_page?: number;
  page?: number;
  sort_by?: OriginUbnAnimalSortBy;
  sort_direction?: 'asc' | 'desc';
}

export const ORIGIN_UBN_ANIMAL_SORT_FIELDS = [
  'days_on_stable',
  'age_at_arrival',
  'age_at_end',
  'growth_per_day',
  'growth_index_pct',
] as const;

export type OriginUbnAnimalSortBy = (typeof ORIGIN_UBN_ANIMAL_SORT_FIELDS)[number];

@Injectable({
  providedIn: 'root'
})
export class OriginUbnService extends BaseService<OriginUbnStats> {
  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/origin-ubns';
  }

  public stats(originUbn: string): Observable<OriginUbnStatsResponse> {
    return this.getChild<OriginUbnStatsResponse>(originUbn.trim(), 'stats');
  }

  public dashboard(originUbn: string): Observable<OriginUbnDashboardResponse> {
    return this.getChild<OriginUbnDashboardResponse>(originUbn.trim(), 'dashboard');
  }

  public animals(
    originUbn: string,
    params: OriginUbnAnimalsParams = {},
  ): Observable<PaginatedResponse<OriginUbnAnimal>> {
    const query: Record<string, string | number> = {};

    if (params.with_growth_only != null) {
      query['with_growth_only'] = params.with_growth_only ? 1 : 0;
    }
    if (params.koppel_type) {
      query['koppel_type'] = params.koppel_type;
    }
    if (params.gender?.trim()) {
      query['gender'] = params.gender.trim().toUpperCase();
    }
    if (params.color?.trim()) {
      query['color'] = params.color.trim().toUpperCase();
    }
    if (params.per_page != null) {
      query['per_page'] = params.per_page;
    }
    if (params.page != null) {
      query['page'] = params.page;
    }
    if (params.sort_by) {
      query['sort_by'] = params.sort_by;
    }
    if (params.sort_direction) {
      query['sort_direction'] = params.sort_direction;
    }

    return this.getChild<PaginatedResponse<OriginUbnAnimal>>(
      originUbn.trim(),
      'animals',
      query,
    );
  }

  public statistics(
    params: OriginUbnStatisticsParams = {},
  ): Observable<PaginatedResponse<OriginUbnStatistic>> {
    const query: Record<string, string | number> = {};

    if (params.sort) {
      query['sort'] = params.sort;
    }
    if (params.direction) {
      query['direction'] = params.direction;
    }
    if (params.per_page != null) {
      query['per_page'] = params.per_page;
    }
    if (params.page != null) {
      query['page'] = params.page;
    }
    if (params.q) {
      query['q'] = params.q;
    }
    if (params.rank) {
      query['rank'] = params.rank;
    }
    if (params.rank_type) {
      query['rank_type'] = params.rank_type;
    }
    if (params.min_animals_with_growth != null) {
      query['min_animals_with_growth'] = params.min_animals_with_growth;
    }

    return this.getAll(query, 'statistics') as Observable<
      PaginatedResponse<OriginUbnStatistic>
    >;
  }

  public syncStatistics(originUbn?: string | null): Observable<OriginUbnSyncResponse> {
    const body = originUbn ? { origin_ubn: originUbn } : {};
    return this.post(body as Partial<OriginUbnStats>, 'statistics/sync');
  }

  public traderRelations(
    loadingUbn: string,
    period: OriginUbnTraderPeriod = 'year',
  ): Observable<OriginUbnTraderRelationsResponse> {
    return this.getChild<OriginUbnTraderRelationsResponse>(
      loadingUbn.trim(),
      'trader-relations',
      { period },
    );
  }

  public traderRelationRides(
    loadingUbn: string,
    relationId: number,
    period: OriginUbnTraderPeriod = 'year',
  ): Observable<OriginUbnTraderRidesResponse> {
    return this.getChild<OriginUbnTraderRidesResponse>(
      loadingUbn.trim(),
      `trader-relations/${relationId}/rides`,
      { period },
    );
  }
}
